import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types/user';
import { logger } from '../utils/logger';
import { api, logout as apiLogout } from '../services/api';
import {
  setAccessToken,
  clearAccessToken,
  getTokenExpiration,
  clearLegacyTokenStorage,
} from '../utils/authToken';
import { restoreSessionFromCookie, refreshAccessToken } from '../utils/sessionRefresh';
import { isMfaChallenge } from '../services/mfaService';

interface MagicLinkTokens {
  access_token: string;
  expires_in: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User | null>;
  completeMagicLinkAuth: (tokens: MagicLinkTokens) => Promise<User | null>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  error: string | null;
  clearError: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  full_name?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user;

  useEffect(() => {
    clearLegacyTokenStorage();
    const bootstrap = async () => {
      const restored = await restoreSessionFromCookie();
      if (restored) {
        await fetchCurrentUser();
      } else {
        setIsLoading(false);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    const checkTokenExpiration = async () => {
      const expiration = getTokenExpiration();
      if (!expiration) return;

      const timeUntilExpiry = expiration - Date.now();
      if (timeUntilExpiry < 300000 && timeUntilExpiry > 0) {
        try {
          logger.debug('Auto-refreshing token...', undefined, 'AUTH');
          const token = await refreshAccessToken();
          if (!token) {
            setUser(null);
          } else {
            logger.debug('Token auto-refreshed successfully', undefined, 'AUTH');
          }
        } catch {
          logger.error('Token auto-refresh failed', undefined, 'AUTH');
          clearAccessToken();
          setUser(null);
        }
      }
    };

    const interval = setInterval(checkTokenExpiration, 60000);
    checkTokenExpiration();
    return () => clearInterval(interval);
  }, []);

  const fetchCurrentUser = async (): Promise<User | null> => {
    try {
      const { data: userData } = await api.get<User>('/v1/users/me');
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Error fetching user:', err);
      clearAccessToken();
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const completeMagicLinkAuth = async (tokens: MagicLinkTokens): Promise<User | null> => {
    setAccessToken(tokens.access_token, tokens.expires_in);
    return await fetchCurrentUser();
  };

  const login = async (email: string, password: string): Promise<User | null> => {
    setError(null);
    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);

      const { data } = await api.post('/v1/auth/login', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      if (isMfaChallenge(data)) {
        const message =
          'Multi-factor authentication is required. Please sign in to continue.';
        setError(message);
        throw new Error(message);
      }

      setAccessToken(data.access_token, data.expires_in);
      return await fetchCurrentUser();
    } catch (err: any) {
      const msg = err.response?.data?.message ?? err.response?.data?.detail ?? err.message ?? 'Login failed';
      const message = typeof msg === 'string' ? msg : 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    setError(null);
    setIsLoading(true);

    try {
      const { data } = await api.post('/v1/auth/register', userData);
      return data;
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.detail || 'Registration failed';
      setError(typeof msg === 'string' ? msg : 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.error('Error revoking refresh token:', err);
    }

    clearAccessToken();
    setUser(null);
    setError(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    completeMagicLinkAuth,
    register,
    logout,
    updateUser,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
